import { Dispatch } from "react";
import { createAsyncThunk } from "@reduxjs/toolkit";

import { BAND_ACTIONS, EventBlocksType, IEventTravelBlockParams } from "Bands/types";
import { EventTravelBlockService } from "Bands/services";
import { API_ENDPOINTS } from "shared/constants";
import { IDataKeys } from "shared/types";

export const dispatchGetEventTravelBlocks = (dispatch: Dispatch<any>, event?: string) => {
  dispatch(
    getEventTravelBlocks({
      event,
      page: 1, // we assume all blocks will be returned in page 1
      itemsPerPage: 100, // avoid pagination
    })
  );
};

export const getEventTravelBlocks = createAsyncThunk(
  BAND_ACTIONS.getEventTravelBlocks,
  async (
    {
      event,
      itemsPerPage,
      page,
    }: {
      event?: string;
      page: number;
      itemsPerPage: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const data = await EventTravelBlockService.getBlocks({
        event: event ? API_ENDPOINTS.EVENT(event) : undefined,
        page,
        itemsPerPage,
      });

      // keep the key even if response is empty
      return {
        [IDataKeys.eventTravelBlock]: data.eventTravelBlock,
        meta: data.meta,
        pagination: data.pagination,
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const dispatchGetEventTravelBlock = (dispatch: Dispatch<any>, blockId: string) => {
  const action = API_ENDPOINTS.EVENT_TRAVEL_BLOCK(blockId);
  return dispatch(getEventTravelBlock({ blockId, action }));
};

export const getEventTravelBlock = createAsyncThunk(
  BAND_ACTIONS.getEventTravelBlock,
  async ({ blockId }: { blockId: string; action: string }, { rejectWithValue }) => {
    try {
      const block = await EventTravelBlockService.getBlock(blockId);

      return block;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const createEventTravelBlock = createAsyncThunk(
  BAND_ACTIONS.createEventTravelBlock,
  async (block: IEventTravelBlockParams, { rejectWithValue }) => {
    try {
      const data = await EventTravelBlockService.createBlock(block);
      const id = Object.values(data?.eventTravelBlock)?.[0]?.attributes?.id;
      return { id, data };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const editEventTravelBlock = createAsyncThunk(
  BAND_ACTIONS.editEventTravelBlock,
  async (props: { id: string; data: IEventTravelBlockParams }, { rejectWithValue }) => {
    try {
      const data = await EventTravelBlockService.editBlock(props);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const editEventTravelBlockTitle = createAsyncThunk(
  BAND_ACTIONS.editEventTravelBlockTitle,
  async (props: { id: string; title: string }, { rejectWithValue }) => {
    try {
      const data = await EventTravelBlockService.editBlockTitle(props);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const deleteEventTravelBlock = createAsyncThunk(
  BAND_ACTIONS.deleteEventTravelBlock,
  async (id: string, { rejectWithValue }) => {
    try {
      await EventTravelBlockService.deleteBlock(id);
      return {
        path: API_ENDPOINTS.EVENT_TRAVEL_BLOCK(id),
        dataKey: IDataKeys.eventTravelBlock,
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getEventSuggestedLocations = createAsyncThunk(
  BAND_ACTIONS.getEventSuggestedLocations,
  async (
    {
      event,
      eventBlockType,
    }: {
      event?: string;
      eventBlockType?: EventBlocksType;
    },
    { rejectWithValue }
  ) => {
    try {
      const data = await EventTravelBlockService.getSuggestedLocations({
        event: event ? API_ENDPOINTS.EVENT(event) : undefined,
        eventBlockType,
      });

      // keep the key even if response is empty
      return {
        [IDataKeys.eventSuggestedLocation]: data.eventSuggestedLocation,
        meta: data.meta,
        pagination: data.pagination,
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getEventGoogleLocations = createAsyncThunk(
  BAND_ACTIONS.getEventGoogleLocations,
  async (
    {
      term,
      eventBlockType,
    }: {
      term?: string;
      eventBlockType?: EventBlocksType;
    },
    { rejectWithValue }
  ) => {
    try {
      const data = await EventTravelBlockService.getSuggestedLocations({
        term,
        eventBlockType,
      });

      // keep the key even if response is empty
      return {
        [IDataKeys.eventGoogleLocation]: data.eventSuggestedLocation,
        meta: data.meta,
        pagination: data.pagination,
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);
